import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API = "https://api.fullagro.com";

const STATUS_STYLE = {
  success: { bg: "#1f4d2c", fg: "#b6f5c2" },
  error:   { bg: "#4d1f1f", fg: "#f5b6b6" },
  running: { bg: "#1f3d4d", fg: "#b6e0f5" },
  cancelled: { bg: "#4d4a1f", fg: "#f5ecb6" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function fmtDuration(ms) {
  if (!ms && ms !== 0) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

export default function SyncPage() {
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);
  const [me, setMe] = useState(null);

  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState({ interval_minutes: 360, retention_days: 30, enabled: true });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/"); return; }
    try {
      const decoded = jwtDecode(token);
      setMe(decoded);
      if (decoded.user_type !== "admin") {
        setDenied(true);
        setLoading(false);
        return;
      }
    } catch {
      navigate("/");
      return;
    }
    refresh();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!status) return;
    const id = setInterval(() => {
      if (status.in_progress) refresh();
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [status?.in_progress]);

  const api = useCallback(async (path, opts = {}) => {
    const token = localStorage.getItem("token");
    const res = await fetch(API + path, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
        ...(opts.headers || {}),
      },
    });
    const text = await res.text();
    let json = {};
    try { json = text ? JSON.parse(text) : {}; } catch { json = { raw: text }; }
    if (!res.ok) {
      const msg = json.message || json.status || `HTTP ${res.status}`;
      throw new Error(msg);
    }
    return json;
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, l] = await Promise.all([
        api("/sync/status"),
        api("/sync/config"),
        api("/sync/logs?limit=5"),
      ]);
      setStatus(s);
      setCfg(c);
      setLogs(l.logs || []);
    } catch (e) {
      toast.error("Error cargando estado: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api("/sync/config", {
        method: "PUT",
        body: JSON.stringify(cfg),
      });
      toast.success("Configuración guardada. Scheduler rearmado.");
      setCfg(res.config);
      await refresh();
      setTick((t) => t + 1);
    } catch (e) {
      toast.error("Error guardando: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await api("/sync/run", { method: "POST" });
      if (res.status === "cancelled") {
        toast.warn("Hay otra sync en curso; se omitió esta corrida.");
      } else {
        toast.success(`Sync iniciada (log #${res.logId}). Refrescando en unos segundos...`);
      }
      await refresh();
      setTimeout(refresh, 4000);
      setTimeout(refresh, 9000);
    } catch (e) {
      toast.error("Error iniciando sync: " + e.message);
    } finally {
      setRunning(false);
    }
  };

  if (denied) {
    return (
      <div style={{ padding: 32, color: "#eee" }}>
        <h2>Acceso denegado</h2>
        <p>Esta sección es solo para administradores.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 32px", color: "#e5ede5", maxWidth: 1200, margin: "0 auto" }}>
      <ToastContainer theme="dark" />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0 }}>Catalog Sync</h1>
          <p style={{ margin: "4px 0 0", color: "#9aa89a" }}>
            Sincroniza el catálogo desde el sheet de Vercel a la base de datos local.
          </p>
        </div>
        <button onClick={refresh} disabled={loading} style={btnSecondary}>
          {loading ? "Cargando…" : "Refrescar"}
        </button>
      </div>

      {/* STATUS CARD */}
      <section style={card}>
        <h2 style={cardTitle}>Estado</h2>
        {!status ? (
          <p style={{ color: "#9aa89a" }}>Cargando…</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
            <Stat label="Enabled" value={status.enabled ? "ON" : "OFF"} accent={status.enabled} />
            <Stat label="Intervalo" value={`${status.interval_minutes} min`} />
            <Stat label="Retención logs" value={`${status.retention_days} días`} />
            <Stat label="En progreso" value={status.in_progress ? "Sí" : "No"} accent={status.in_progress} />
            <Stat label="Última corrida" value={fmtDate(status.last_run?.finished_at)} sub={status.last_run?.status} />
            <Stat label="Próxima corrida" value={fmtDate(status.next_run_at)} />
          </div>
        )}
        <div style={{ marginTop: 20, display: "flex", gap: 12 }}>
          <button onClick={handleRun} disabled={running || status?.in_progress} style={btnPrimary}>
            {running ? "Iniciando…" : status?.in_progress ? "Sync en curso…" : "Run now"}
          </button>
        </div>
      </section>

      {/* CONFIG CARD */}
      <section style={card}>
        <h2 style={cardTitle}>Configuración</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          <Field label="Intervalo (minutos)" hint="1 – 10080 (7 días)">
            <input
              type="number"
              min={1}
              max={10080}
              value={cfg.interval_minutes ?? ""}
              onChange={(e) => setCfg({ ...cfg, interval_minutes: parseInt(e.target.value, 10) || 0 })}
              style={input}
            />
          </Field>
          <Field label="Retención de logs (días)" hint="1 – 365">
            <input
              type="number"
              min={1}
              max={365}
              value={cfg.retention_days ?? ""}
              onChange={(e) => setCfg({ ...cfg, retention_days: parseInt(e.target.value, 10) || 0 })}
              style={input}
            />
          </Field>
          <Field label="Scheduler habilitado">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={!!cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              />
              <span>{cfg.enabled ? "Activado" : "Desactivado"}</span>
            </label>
          </Field>
        </div>
        <div style={{ marginTop: 20 }}>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </section>

      {/* LOGS CARD */}
      <section style={card}>
        <h2 style={cardTitle}>Últimas 5 corridas</h2>
        {logs.length === 0 ? (
          <p style={{ color: "#9aa89a" }}>Sin corridas registradas todavía.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Inicio</th>
                  <th style={th}>Fin</th>
                  <th style={th}>Estado</th>
                  <th style={th}>Origen</th>
                  <th style={th}>Fetched</th>
                  <th style={th}>Insert</th>
                  <th style={th}>Update</th>
                  <th style={th}>Unchanged</th>
                  <th style={th}>Fail</th>
                  <th style={th}>Duración</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td style={td}>{fmtDate(l.started_at)}</td>
                    <td style={td}>{fmtDate(l.finished_at)}</td>
                    <td style={td}>
                      <Pill status={l.status} />
                    </td>
                    <td style={td}>{l.triggered_by}</td>
                    <td style={tdNum}>{l.fetched ?? "—"}</td>
                    <td style={tdNum}>{l.inserted ?? "—"}</td>
                    <td style={tdNum}>{l.updated ?? "—"}</td>
                    <td style={tdNum}>{l.unchanged ?? "—"}</td>
                    <td style={tdNum}>{l.failed ?? "—"}</td>
                    <td style={td}>{fmtDuration(l.duration_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {logs.some((l) => l.error_message) && (
          <div style={{ marginTop: 16 }}>
            {logs.filter((l) => l.error_message).slice(0, 1).map((l) => (
              <pre key={l.id} style={errorBox}>{l.error_message}</pre>
            ))}
          </div>
        )}
      </section>

      <div style={{ marginTop: 24, color: "#7d8a7d", fontSize: 13 }}>
        Usuario: <code>{me?.username || me?.email || "—"}</code> · token expira automáticamente con JWT.
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div style={{ padding: 12, background: "#0e130e", border: "1px solid #1c271c", borderRadius: 8 }}>
      <div style={{ fontSize: 12, color: "#9aa89a", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: accent ? "#7ee787" : "#e5ede5", marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#9aa89a", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: "#9aa89a", marginBottom: 6 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#7d8a7d", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function Pill({ status }) {
  const s = STATUS_STYLE[status] || { bg: "#222", fg: "#ccc" };
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 999,
      background: s.bg,
      color: s.fg,
      fontSize: 12,
      fontWeight: 600,
      textTransform: "uppercase",
    }}>{status}</span>
  );
}

const card = {
  background: "#0a0e0a",
  border: "1px solid #1c271c",
  borderRadius: 12,
  padding: 24,
  marginBottom: 20,
};

const cardTitle = { margin: "0 0 16px", fontSize: 16, fontWeight: 600, color: "#e5ede5" };

const input = {
  width: "100%",
  padding: "8px 10px",
  background: "#0e130e",
  border: "1px solid #2a3a2a",
  borderRadius: 6,
  color: "#e5ede5",
  fontSize: 14,
  boxSizing: "border-box",
};

const btnPrimary = {
  padding: "10px 18px",
  background: "#2e7d32",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
};

const btnSecondary = {
  padding: "8px 14px",
  background: "transparent",
  color: "#b6e0b6",
  border: "1px solid #2e7d32",
  borderRadius: 8,
  cursor: "pointer",
};

const table = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th = { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #2a3a2a", color: "#9aa89a", fontWeight: 600 };
const td = { padding: "8px 10px", borderBottom: "1px solid #1c271c", color: "#e5ede5" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

const errorBox = {
  background: "#2a0e0e",
  color: "#f5b6b6",
  padding: 12,
  borderRadius: 6,
  fontSize: 12,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};
